import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, firstValueFrom } from 'rxjs';
import { AuthService } from './auth.service';
import { SettingsService } from './settings.service';
import { environment } from '../../environments/environment';

export interface InitializeAddCardPaymentRequest {
  callbackUrl: string;
}

export interface InitializeAddCardPaymentResponse {
  success: boolean;
  message: string;
  data?: {
    authorizationUrl: string;
    reference: string;
    gateway: string;
  };
}

export interface ArkifiHubCustomerCardDto {
  id: string;
  platformId: string;
  arkifiHubCustomerId: string;
  customerCode: string;
  cardName?: string;
  last4?: string;
  cardType?: string;
  bank?: string;
  brand?: string;
  environmentType: number; // 1 = Test, 2 = Live
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface ApiResult<T> {
  success: boolean;
  message: string;
  data?: T;
}

@Injectable({
  providedIn: 'root'
})
export class CustomerCardService {
  private baseUrl: string = `${environment.apiUrl}/api/ArkifiHubCustomerCard`;

  constructor(
    private http: HttpClient,
    private authService: AuthService,
    private settingsService: SettingsService
  ) {}

  private getAuthHeaders(): HttpHeaders {
    const token = this.authService.getToken();
    if (!token) {
      throw new Error('No authentication token available. Please sign in.');
    }
    return new HttpHeaders({
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    });
  }

  /**
   * Initialize Paystack payment for card verification (₦100 charge)
   */
  async initializeAddCardPayment(
    platformId: string,
    environment: 'Test' | 'Live',
    callbackUrl: string
  ): Promise<ApiResult<InitializeAddCardPaymentResponse['data']>> {
    try {
      const url = `${this.baseUrl}/initialize?platformId=${platformId}&environment=${environment === 'Live' ? 2 : 1}`;
      const body: InitializeAddCardPaymentRequest = { callbackUrl };
      
      const response = await firstValueFrom(
        this.http.post<ApiResult<InitializeAddCardPaymentResponse['data']>>(
          url,
          body,
          { headers: this.getAuthHeaders() }
        )
      );

      return response;
    } catch (error: any) {
      console.error('Error initializing add card payment:', error);
      return {
        success: false,
        message: error.error?.message || 'Failed to initialize payment',
        data: undefined
      };
    }
  }

  /**
   * Verify Paystack transaction and save customer card
   */
  async verifyAndSaveCard(
    platformId: string,
    environment: 'Test' | 'Live',
    transactionReference: string
  ): Promise<ApiResult<ArkifiHubCustomerCardDto>> {
    try {
      const url = `${this.baseUrl}/verify?platformId=${platformId}&environment=${environment === 'Live' ? 2 : 1}&transactionReference=${encodeURIComponent(transactionReference)}`;
      
      const response = await firstValueFrom(
        this.http.post<ApiResult<ArkifiHubCustomerCardDto>>(
          url,
          {},
          { headers: this.getAuthHeaders() }
        )
      );

      return response;
    } catch (error: any) {
      console.error('Error verifying and saving card:', error);
      return {
        success: false,
        message: error.error?.message || 'Failed to verify and save card',
        data: undefined
      };
    }
  }

  /**
   * Get all active cards for the authenticated customer
   */
  async getCustomerCards(
    platformId: string,
    environment?: 'Test' | 'Live'
  ): Promise<ApiResult<ArkifiHubCustomerCardDto[]>> {
    try {
      // Check authentication first
      if (!this.authService.isAuthenticated()) {
        return {
          success: false,
          message: 'You must be signed in to view your cards',
          data: []
        };
      }

      let url = `${this.baseUrl}/cards?platformId=${platformId}`;
      if (environment) {
        url += `&environment=${environment === 'Live' ? 2 : 1}`;
      }
      
      const response = await firstValueFrom(
        this.http.get<ApiResult<ArkifiHubCustomerCardDto[]>>(
          url,
          { headers: this.getAuthHeaders() }
        )
      );

      return response;
    } catch (error: any) {
      console.error('Error getting customer cards:', error);
      
      // Handle 401 Unauthorized - token might be expired
      if (error.status === 401 || error.error?.message?.includes('Invalid authentication token')) {
        return {
          success: false,
          message: 'Your session has expired. Please sign in again.',
          data: []
        };
      }
      
      return {
        success: false,
        message: error.error?.message || error.message || 'Failed to retrieve cards',
        data: []
      };
    }
  }

  /**
   * Deactivate a customer card
   */
  async deactivateCard(cardId: string): Promise<ApiResult<boolean>> {
    try {
      const url = `${this.baseUrl}/cards/${cardId}`;
      
      const response = await firstValueFrom(
        this.http.delete<ApiResult<boolean>>(
          url,
          { headers: this.getAuthHeaders() }
        )
      );

      return response;
    } catch (error: any) {
      console.error('Error deactivating card:', error);
      return {
        success: false,
        message: error.error?.message || 'Failed to deactivate card',
        data: false
      };
    }
  }
}
